import classNames from "classnames";
import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Tab from "components/DesignSystem/Tab/Tab";
import Table from "components/DesignSystem/Table/V2/Table";
import { DownloadIcon } from "components/icons/Download";
import { PricedTicket } from "components/icons/PricedTicket";
import { UnpricedTicket } from "components/icons/UnpricedTicket";
import Wind from "components/icons/Wind";
import { EmptyList } from "components/Illustrations/EmptyList";
import { TableEmptyState } from "components/TableEmptyState/TableEmptyState";
import { CreateTicket } from "components/Ticket/CreateTicket";
import { ticketColumns } from "components/Ticket/TicketCells";
import { TicketFilter } from "components/Ticket/TicketFilter";
import { INKLE_TEAM_AND_CPA_TEAM } from "constants/chatType";
import {
  TICKET_CREATE_END_DATE,
  TICKET_CREATE_SELECT_PERIOD,
  TICKET_CREATE_START_DATE,
  TICKET_ETD_END_DATE,
  TICKET_ETD_SELECT_PERIOD,
  TICKET_ETD_START_DATE,
  TICKET_SORT_OPTIONS,
  TICKET_RESOLVED_END_DATE,
  TICKET_RESOLVED_SELECT_PERIOD,
  TICKET_RESOLVED_START_DATE,
} from "constants/ticket";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { flexRender, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import {
  useGetChatTicketsQuery,
  useLazyGetChatTicketReportsQuery,
} from "store/apis/chatTicket";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import {
  closeCreateChatTicketModal,
  openCreateChatTicketModal,
} from "store/slices/ticket";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { TicketSliderFromHome } from "./TicketSliderFromHome";

export const ActionableItems = ({
  defaultAssigneeProfileId = null,
}: {
  defaultAssigneeProfileId?: string | null;
}) => {
  const queryData = useQuery();
  const dispatch = useDispatch();
  const { isAdmin, isCustomer, canManageInternalTickets } = useRoleBasedView();
  const createTicketModal = useModal();
  const [resolvedStatus, setResolvedStatus] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [isTabSwitched, setIsTabSwitched] = useState(false);

  const selectedTag = queryData.get("tag_filter") || null;
  const selectedAssignee = queryData.get("assignee_profile_ids") || null;
  const selectedAgent = queryData.get("agent_ids") || null;
  const selectedTicketType = queryData.get("ticket_type") || null;
  const selectedTicketETD = queryData.get("ticket_etd") || null;
  const selectedPriority = queryData.get("priority") || null;
  const selectedETDStartDate = queryData.get(TICKET_ETD_START_DATE) || null;
  const selectedETDEndDate = queryData.get(TICKET_ETD_END_DATE) || null;
  const selectedTask = queryData.get("task_name") || null;
  const selectedSortBy = queryData.get("sort_by") || null;
  const selectedCreateStartDate =
    queryData.get(TICKET_CREATE_START_DATE) || null;
  const selectedCreateEndDate = queryData.get(TICKET_CREATE_END_DATE) || null;
  const selectedCreatedBy = queryData.get("created_by_profile_ids") || null;
  const selectedResolvedStartDate =
    queryData.get(TICKET_RESOLVED_START_DATE) || null;
  const selectedResolvedEndDate =
    queryData.get(TICKET_RESOLVED_END_DATE) || null;

  const { update, updateMultiple } = useUpdateQuery();

  const isUnassignedAgentSelected = selectedAgent
    ?.split(",")
    .some((v) => v === "unassigned");

  const [selectedSort, setSelectedSort] = useState<string>(
    selectedSortBy || TICKET_SORT_OPTIONS.TICKET_TYPE_NEAREST_ETD_SORT.value
  );

  const params = {
    page_num: pageNumber,
    assignee_profile_ids: selectedAssignee || null,
    agent_ids:
      selectedAgent
        ?.split(",")
        .filter((v) => v !== "unassigned")
        .join() || null,
    company_group_uuids: null,
    resolved_status: resolvedStatus,
    search_term: searchValue,
    tag_filter: selectedTag || null,
    type_filters: selectedTicketType || null,
    etd_filters: selectedTicketETD || null,
    sort_by:
      selectedSort ||
      (resolvedStatus
        ? TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT.value
        : TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.value),
    priority: selectedPriority || null,
    etd_start_date: selectedETDStartDate,
    etd_end_date: selectedETDEndDate,
    task_name: selectedTask,
    created_start_date: selectedCreateStartDate,
    created_end_date: selectedCreateEndDate,
    unassigned_spoc_tickets: isUnassignedAgentSelected || null,
    is_internal_ticket: true,
    created_by_profile_ids: selectedCreatedBy || null,
    resolved_start_date: selectedResolvedStartDate,
    resolved_end_date: selectedResolvedEndDate,
  };

  const {
    data,
    isLoading: isChatTicketsLoading,
    isFetching: isChatTicketsFetching,
    isSuccess,
  } = useGetChatTicketsQuery(params);

  const tickets = useMemo(() => data?.tickets ?? [], [data?.tickets]);

  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [downloadReport] = useLazyGetChatTicketReportsQuery();
  const { successToast, alertToast } = useToast();

  const { data: assignees = [] } = useGetServiceTeamQuery(
    {
      accessible_teams: canManageInternalTickets
        ? INKLE_TEAM_AND_CPA_TEAM
        : null,
    },
    { skip: isCustomer }
  );

  const { data: callAgents = [] } = useGetAssignAgentsQuery(
    {
      agentType: "CALL_EXECUTIVE",
    },
    { skip: !isAdmin }
  );

  const { data: tags = [] } = useGetTagsForCategoryQuery({
    tagType: "TICKETS_TAG",
  });

  const onSortTabSelect = (sortBy: string) => {
    setSelectedSort(sortBy);
    update({ query: "sort_by", value: sortBy });
  };

  useEffect(() => {
    if (!selectedAssignee && defaultAssigneeProfileId) {
      update({
        query: "assignee_profile_ids",
        value: defaultAssigneeProfileId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAssigneeProfileId]);

  useEffect(() => {
    if (!isChatTicketsFetching) {
      setIsTabSwitched(false);
    }
  }, [isTabSwitched, isChatTicketsFetching]);

  const onSearchQueryChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchValue(value.toLowerCase() || "");
  });

  const onExportActionables = async () => {
    if (tickets.length === 0) {
      alertToast({ message: "No actionable found to export" });
      return;
    }

    setIsDownloadingReport(true);
    try {
      const downloadUrl = await downloadReport({
        ...params,
        action: "EXPORT_TICKETS",
      }).unwrap();

      window.open(downloadUrl, "_blank");
      successToast({ message: "Actionables report is downloading" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
    setIsDownloadingReport(false);
  };

  const renderTicketTypeIcon = (isPricedTicket: boolean) =>
    isPricedTicket ? <PricedTicket /> : <UnpricedTicket />;

  const tableData = tickets;

  const table = useReactTable({
    data: tableData,
    columns: ticketColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
    state: { columnPinning: { left: ["title"] } },
    initialState: {
      columnVisibility: {
        tags: true,
        resolved_at: false,
        created_on: true,
        assignee: true,
        title: true,
        tasks: false,
        priority: false,
        created_by: true,
      },
    },
    meta: { renderTicketTypeIcon, resolvedStatus, tags, callAgents, assignees },
  });

  const isAnyFilterActive =
    !!selectedTag ||
    !!selectedAssignee ||
    !!selectedAgent ||
    !!selectedTicketType ||
    !!selectedTicketETD ||
    !!selectedPriority ||
    !!selectedETDStartDate ||
    !!selectedETDEndDate ||
    !!selectedTask ||
    !!selectedCreateStartDate ||
    !!selectedCreateEndDate ||
    !!selectedCreatedBy ||
    !!selectedResolvedStartDate ||
    !!selectedResolvedEndDate;

  const resetFilter = () => {
    updateMultiple([
      { query: "tag_filter", value: null },
      { query: "assignee_profile_ids", value: null },
      { query: "agent_ids", value: null },
      { query: "ticket_type", value: null },
      { query: "ticket_etd", value: null },
      { query: "priority", value: null },
      { query: TICKET_ETD_START_DATE, value: null },
      { query: TICKET_ETD_END_DATE, value: null },
      { query: TICKET_ETD_SELECT_PERIOD, value: null },
      { query: "task_name", value: null },
      { query: TICKET_CREATE_START_DATE, value: null },
      { query: TICKET_CREATE_END_DATE, value: null },
      { query: TICKET_CREATE_SELECT_PERIOD, value: null },
      { query: "sort_by", value: null },
      { query: "created_by_profile_ids", value: null },
      { query: TICKET_RESOLVED_START_DATE, value: null },
      { query: TICKET_RESOLVED_END_DATE, value: null },
      { query: TICKET_RESOLVED_SELECT_PERIOD, value: null },
    ]);
    setPageNumber(1);
  };

  return (
    <div className="t-h-full">
      <div className="t-relative t-top-0 t-h-full t-flex t-flex-col t-pb-4">
        <div className="t-sticky t-top-0 t-bg-white t-z-header t-pb-2">
          <div className="t-flex t-items-center t-justify-between sm:t-flex-wrap sm:t-gap-2">
            <Tab.Root value="MATCH">
              <Tab.List>
                <Tab.Trigger
                  value={!resolvedStatus ? "MATCH" : "UNMATCH"}
                  onClick={() => {
                    setResolvedStatus(false);
                    setIsTabSwitched(true);
                    table.setColumnVisibility({ resolved_at: false });
                    setSelectedSort(
                      TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.value
                    );
                  }}
                >
                  Unresolved
                </Tab.Trigger>
                <Tab.Trigger
                  value={resolvedStatus ? "MATCH" : "UNMATCH"}
                  onClick={() => {
                    setResolvedStatus(true);
                    setIsTabSwitched(true);
                    table.setColumnVisibility({ resolved_at: true });
                    setSelectedSort(
                      TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT.value
                    );
                  }}
                >
                  Resolved
                </Tab.Trigger>
              </Tab.List>
            </Tab.Root>
            <Button
              size="small"
              onClick={() => {
                dispatch(
                  openCreateChatTicketModal({
                    showCreateTicketModal: true,
                    isInternalTicket: true,
                    messageId: "",
                    channelId: "",
                  })
                );
                createTicketModal.open();
              }}
            >
              <span
                style={{ fontSize: "18px", lineHeight: 1, marginRight: "4px" }}
              >
                +
              </span>
              Add Internal Ticket
            </Button>
          </div>

          <div className="t-mt-3">
            <Search
              className="t-w-[440px] sm:t-w-full"
              onChange={onSearchQueryChange}
              placeholder="Search"
            />
          </div>

          <div className="t-mt-3 t-flex t-items-center t-gap-2 t-relative t-justify-between t-bg-white sm:t-flex-wrap">
            <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap">
              <TicketFilter
                onSortTabSelect={onSortTabSelect}
                selectedTicketETD={selectedTicketETD}
                selectedSort={selectedSort}
                resolvedStatus={resolvedStatus}
              />
              <Button
                onClick={onExportActionables}
                customType="icon"
                size="xsmall"
                isLoading={isDownloadingReport}
                disabled={isDownloadingReport}
              >
                <span className="t-text-text-30">
                  <DownloadIcon color="currentColor" size={20} />
                </span>
              </Button>
            </div>
            <div className="t-flex t-justify-between t-items-center t-gap-4">
              {isAnyFilterActive && (
                <Button size="xsmall" onClick={resetFilter}>
                  Clear all filters
                </Button>
              )}
              {data && (
                <PaginationHandler
                  page_num={data.current_page}
                  per_page={data.per_page}
                  total_count={data.total_count}
                  total_pages={data.total_pages}
                  isLoading={isChatTicketsFetching && !isChatTicketsLoading}
                  onPageNumChange={(page) => setPageNumber(page)}
                />
              )}
            </div>
          </div>
        </div>

        <div className="t-w-full t-flex-1 t-mt-6 t-overflow-x-auto t-bg-white">
          <Table.Container
            sticky
            className="t-min-w-[120rem] t-max-w-full t-h-full"
          >
            <Table.Content>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id} className="t-z-[11]">
                    {headerGroup?.headers?.map((header, idx) => {
                      const styles = getCommonPinningStyles(header.column);
                      return (
                        <Table.HeadCell
                          key={header.id}
                          style={{
                            ...styles,
                            width: `${header.getSize()}%`,
                            zIndex: idx === 0 ? 20 : undefined,
                          }}
                          className={classNames({
                            "t-sticky t-left-0 t-z-20 t-bg-white t-py-3 t-px-1.5 t-drop-shadow-table-left-pin":
                              idx === 0,
                          })}
                        >
                          <span className="t-flex t-items-center t-gap-1">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {idx < headerGroup?.headers.length - 2 && (
                              <Table.HeadCellSeparator />
                            )}
                          </span>
                        </Table.HeadCell>
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Head>
              <Async.Root
                isLoading={isChatTicketsLoading}
                isSuccess={isSuccess}
                isEmpty={tickets.length === 0}
                customLoader={
                  <div className="t-fixed t-inset-0 t-flex t-justify-center t-items-center t-mt-40">
                    <Loader />
                  </div>
                }
              >
                <Async.Empty>
                  <div className="t-flex t-justify-center t-items-center t-py-16">
                    {isAnyFilterActive ? (
                      <div className="t-flex t-flex-col t-justify-center t-items-center t-w-[400px]">
                        <Wind className="t-text-neutral-40" />
                        <div className="t-text-subtext t-text-text-60 t-mt-4">
                          No actionable found
                        </div>
                        <div className="t-text-body-sm t-text-text-30 t-mb-6">
                          Try adjusting or clearing your filters to see more
                          results
                        </div>
                        <Button size="small" onClick={resetFilter}>
                          Clear all filters
                        </Button>
                      </div>
                    ) : (
                      <div className="t-flex t-flex-col t-justify-center t-items-center t-w-[400px]">
                        <TableEmptyState
                          title="No actionable found"
                          icon={<EmptyList />}
                        />
                      </div>
                    )}
                  </div>
                </Async.Empty>
                <Async.Success>
                  <Table.Body>
                    {table.getRowModel().rows.map((row) => {
                      const {
                        original: {
                          uuid,
                          is_etd_overdue: isEtdOverdue,
                          is_etd_upcoming: isEtdDueSoon,
                        },
                      } = row;

                      return (
                        <Table.Row
                          key={row.id}
                          className={classNames(
                            "t-group hover:t-bg-surface-lighter-grey t-cursor-pointer",
                            {
                              "t-bg-red-10": isEtdOverdue && !resolvedStatus,
                              "t-bg-yellow-10": isEtdDueSoon && !resolvedStatus,
                            }
                          )}
                          onClick={() => setSelectedTicketId(uuid)}
                        >
                          {row.getVisibleCells().map((cell, idx) => {
                            const styles = getCommonPinningStyles(cell.column);
                            return (
                              <Table.Cell
                                key={cell.id}
                                style={{
                                  ...styles,
                                  width: `${cell.column.getSize()}%`,
                                  zIndex: idx === 0 ? 5 : undefined,
                                }}
                                className={classNames({
                                  "group-hover:!t-bg-surface-lighter-grey t-z-[5] t-drop-shadow-table-left-pin":
                                    idx === 0,
                                  "!t-bg-red-10":
                                    isEtdOverdue &&
                                    idx === 0 &&
                                    !resolvedStatus,
                                  "!t-bg-yellow-10":
                                    isEtdDueSoon &&
                                    idx === 0 &&
                                    !resolvedStatus,
                                })}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Table.Cell>
                            );
                          })}
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Async.Success>
              </Async.Root>
            </Table.Content>
          </Table.Container>

          {selectedTicketId && (
            <TicketSliderFromHome
              ticketId={selectedTicketId}
              isOpen={Boolean(selectedTicketId)}
              close={() => setSelectedTicketId(null)}
            />
          )}

          {createTicketModal.isOpen && (
            <CreateTicket
              isNonChatTicket={true}
              show={createTicketModal.isOpen}
              closeModal={() => {
                dispatch(closeCreateChatTicketModal());
                createTicketModal.close();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
